const DEFAULT_TINT = 'rgba(41, 98, 255, 0.12)'
const DEFAULT_SYNTHETIC_BAR_SPACING_PX = 6

export function buildCarsRegimeSpans(regime, bars) {
  if (!Array.isArray(regime) || !Array.isArray(bars) || bars.length === 0) return []
  const spans = []
  let runStart = null
  let runEnd = null
  const limit = Math.min(regime.length, bars.length)

  for (let i = 0; i < limit; i++) {
    const barTime = bars[i]?.time
    const isOn = regime[i] === true && Number.isFinite(barTime)
    if (isOn) {
      if (runStart == null) runStart = barTime
      runEnd = barTime
      continue
    }
    if (runStart != null) {
      spans.push({ startTime: runStart, endTime: runEnd })
      runStart = null
      runEnd = null
    }
  }

  if (runStart != null) spans.push({ startTime: runStart, endTime: runEnd })
  return spans
}

// Keep the full signal history when clipping, so the visible edge cannot invent a flip.
export function buildSignalRegimeSpans(regimes, direction, {from=-Infinity,to=Infinity}={}) {
  const known = value => [-1,0,1].includes(value)
  const starts = new Set(regimes.filter((row,i) => i>0 && row.direction===direction && known(regimes[i-1].direction) && regimes[i-1].direction!==direction).map(row=>row.time))
  const visible = regimes.filter(row=>row.time>=from&&row.time<=to)
  return buildCarsRegimeSpans(visible.map(row=>row.direction===direction),visible).map(span=>({...span,highlightStart:starts.has(span.startTime)}))
}

function readBarSpacing(timeScale) {
  try {
    const spacing = timeScale?.options?.()?.barSpacing
    if (Number.isFinite(spacing) && spacing > 0) return spacing
  } catch { /* noop */ }
  return DEFAULT_SYNTHETIC_BAR_SPACING_PX
}

function resolveBitmapHeight(scope) {
  if (Number.isFinite(scope?.bitmapSize?.height)) return scope.bitmapSize.height
  if (Number.isFinite(scope?.mediaSize?.height) && Number.isFinite(scope?.verticalPixelRatio)) {
    return scope.mediaSize.height * scope.verticalPixelRatio
  }
  return scope?.context?.canvas?.height ?? 0
}

export class CarsRegimePrimitive {
  constructor() {
    this._data = { spans: [], fillStyle: DEFAULT_TINT }
    this._chart = null
    this._requestUpdate = null
    this._paneViews = [new CarsRegimePaneView(this)]
  }

  attached(param) {
    this._chart = param.chart
    this._requestUpdate = param.requestUpdate
  }

  detached() {
    this._chart = null
    this._requestUpdate = null
    this._data = { spans: [], fillStyle: DEFAULT_TINT }
  }

  update(next) {
    this._data = {
      spans: Array.isArray(next?.spans) ? next.spans : [],
      fillStyle: next?.fillStyle || DEFAULT_TINT,
      ...(next?.startFillStyle ? {startFillStyle:next.startFillStyle} : {}),
    }
    if (typeof this._requestUpdate === 'function') this._requestUpdate()
  }

  updateAllViews() { /* renderer reads live state */ }

  paneViews() {
    return this._paneViews
  }
}

class CarsRegimePaneView {
  constructor(primitive) {
    this._renderer = new CarsRegimePaneRenderer(primitive)
  }
  zOrder() { return 'bottom' }
  renderer() { return this._renderer }
}

class CarsRegimePaneRenderer {
  constructor(primitive) {
    this._primitive = primitive
  }

  draw() { /* background renderer only */ }

  drawBackground(target) {
    this._drawTint(target)
  }

  _drawTint(target) {
    const data = this._primitive._data
    const chart = this._primitive._chart
    if (!data?.spans?.length || !chart) return

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context
      const timeScale = chart.timeScale()
      const spacing = readBarSpacing(timeScale) * scope.horizontalPixelRatio
      const halfSpacing = spacing / 2
      const height = resolveBitmapHeight(scope)
      if (!Number.isFinite(height) || height <= 0) return

      ctx.save()
      ctx.fillStyle = data.fillStyle
      for (const span of data.spans) {
        const startCoord = timeScale.timeToCoordinate(span.startTime)
        const endCoord = timeScale.timeToCoordinate(span.endTime)
        if (startCoord == null || endCoord == null) continue
        const x1 = Math.min(startCoord, endCoord) * scope.horizontalPixelRatio - halfSpacing
        const x2 = Math.max(startCoord, endCoord) * scope.horizontalPixelRatio + halfSpacing
        if (span.highlightStart && data.startFillStyle) {
          const split = Math.min(x2, x1 + spacing)
          ctx.fillStyle = data.startFillStyle
          ctx.fillRect(x1, 0, Math.max(1, split-x1), height)
          ctx.fillStyle = data.fillStyle
          if (split < x2) ctx.fillRect(split, 0, x2-split, height)
        } else {
          ctx.fillStyle = data.fillStyle
          ctx.fillRect(x1, 0, Math.max(1, x2-x1), height)
        }
      }
      ctx.restore()
    })
  }
}
